import React from "react";
import StatusBall from "src/components/statusball/statusball.v1";
import { WeatherIcon } from "../../utils/shared-components";
import { 
    getWeatherForecast, 
    getConditionsLiftsAndTrails, 
    getConditionsSlopes, 
    getWeatherForecastSymbol,
    splitNumberAndUnits,
    getReportTimespan,
    getNextTimespan
} from "../../service/liveconditions.api";
import { TSkitudeAPIResponse } from "../../service/liveconditions.types";

// Compact shimmer for loading state
export const Shimmer = ({ width = "w-8" }: { width?: string }) => (
    <div className={`animate-pulse bg-current opacity-20 rounded h-4 ${width}`}></div>
);

// Helper to get weather data row
const getWeatherDataRow = (forecast: any, key: string) => {
    if (!forecast) return null;
    const timespan = getReportTimespan();
    const ntimespan = getNextTimespan(timespan);
    const weather = forecast?.report?.[timespan] || forecast?.report?.[ntimespan];
    if (!weather || !Array.isArray(weather)) return null;
    return weather.find((row: any) => row.key === key);
};

// Compact Status: dot + Open/Closed
export const CompactStatus = ({ isOpen, isLoading }: { isOpen: boolean; isLoading: boolean }) => {
    if (isLoading) return <Shimmer width="w-12" />;
    return (
        <span className="flex items-center gap-1">
            <StatusBall open={isOpen} />
            <span className="text-xs font-medium">{isOpen ? "Open" : "Closed"}</span>
        </span>
    );
};

// Compact Weather: icon + temperature
export const CompactWeather = ({ 
    resortData, 
    units = "metric", 
    isLoading 
}: { 
    resortData: TSkitudeAPIResponse | null; 
    units?: string;
    isLoading: boolean;
}) => {
    if (isLoading) return <Shimmer width="w-14" />;
    if (!resortData) return null;
    
    const todayForecast = getWeatherForecast(resortData, 0);
    if (!todayForecast) return null;
    
    const weatherSymbol = getWeatherForecastSymbol(todayForecast) || "unknown";
    const maxTempRow = getWeatherDataRow(todayForecast, "maximum_temperature");
    if (!maxTempRow) return null;
    
    const isImperial = units === "imperial";
    const tempData = isImperial 
        ? splitNumberAndUnits(maxTempRow.value_fahrenheit)
        : splitNumberAndUnits(maxTempRow.value_celsius);
    
    if (!tempData?.number) return null;
    
    return (
        <span className="flex items-center gap-1">
            <WeatherIcon weatherSymbol={weatherSymbol} size={16} />
            <span className="text-xs font-medium">{tempData.number}{tempData.units || ""}</span>
        </span>
    );
};

// Lift icon SVG
const LiftIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4v16M20 4v16M4 12h16M8 8v8M16 8v8" strokeLinecap="round" />
    </svg>
);

// Compact Lifts: icon + open/total
export const CompactLifts = ({ 
    resortData, 
    timeOfYear = "winter",
    isLoading 
}: { 
    resortData: TSkitudeAPIResponse | null; 
    timeOfYear?: "winter" | "summer" | "always";
    isLoading: boolean;
}) => {
    if (isLoading) return <Shimmer width="w-10" />;
    if (!resortData) return null;
    
    const lifts = getConditionsLiftsAndTrails(resortData, timeOfYear);
    if (!lifts) return null;
    
    const openLifts = lifts.status?.open || 0;
    const totalLifts = lifts.total || 0;
    
    if (totalLifts === 0) return null;
    
    return (
        <span className="flex items-center gap-1">
            <LiftIcon />
            <span className="text-xs font-medium">{openLifts}/{totalLifts}</span>
        </span>
    );
};

// Slopes icon SVG
const SlopesIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M2 22L22 2M6 22l12-16M10 22l8-10M14 22l4-4" strokeLinecap="round" />
    </svg>
);

// Compact Slopes/Trails: icon + open/total
export const CompactSlopes = ({ 
    resortData, 
    timeOfYear = "winter",
    isLoading 
}: { 
    resortData: TSkitudeAPIResponse | null; 
    timeOfYear?: "winter" | "summer" | "always";
    isLoading: boolean;
}) => {
    if (isLoading) return <Shimmer width="w-10" />;
    if (!resortData) return null;
    
    const slopes = getConditionsSlopes(resortData, timeOfYear);
    if (!slopes) return null;
    
    const openSlopes = slopes.status?.open || 0;
    const totalSlopes = slopes.total || 0;
    
    if (totalSlopes === 0) return null;
    
    return (
        <span className="flex items-center gap-1">
            <SlopesIcon />
            <span className="text-xs font-medium">{openSlopes}/{totalSlopes}</span>
        </span>
    );
};

